import { cn } from '@/lib/utils';

/**
 * Общие стили для компонентов ArendaPro+
 * Используйте эти константы для обеспечения единообразия стилей
 * и уменьшения дублирования Tailwind классов
 */

// Стили для кнопок
export const buttonStyles = {
  // Основные варианты кнопок
  icon: "h-8 w-8 p-0",
  iconSmall: "h-6 w-6 p-0",
  iconMicro: "h-4 w-4 p-0",
  iconButton: "h-8 w-8 p-0",
  actionButton: "flex items-center gap-2",
  primary: "bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2",
  secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 h-10 px-4 py-2",
  // Стили для групп кнопок
  buttonGroup: "flex items-center gap-2",
  buttonBar: "flex items-center justify-end gap-2",
};

// Стили для карточек
export const cardStyles = {
  default: "bg-card rounded-lg border shadow-sm",
  content: "p-6",
  contentCompact: "p-4",
  header: "flex flex-row items-center justify-between space-y-0 pb-2",
  title: "text-xl font-semibold tracking-tight",
  description: "text-sm text-muted-foreground",
  feature: "max-w-5xl w-full rounded-3xl bg-white/90 backdrop-blur-md shadow-2xl p-6 md:p-12",
  container: "space-y-4",
  card: "w-full p-4 border rounded-lg",
  error: "p-4 border border-red-200 rounded-lg bg-red-50 text-red-700",
};

// Стили для таблиц
export const tableStyles = {
  container: "w-full overflow-auto border rounded-lg overflow-hidden bg-white",
  wrapper: "rounded-md border",
  header: "bg-muted/50",
  headerCell: "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
  row: "hover:bg-muted/50 data-[state=selected]:bg-muted",
  rowSelected: "bg-muted/30 hover:bg-muted/50",
  rowBordered: "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
  cell: "p-4 align-middle [&:has([role=checkbox])]:pr-0",
  cellCompact: "p-2 align-middle [&:has([role=checkbox])]:pr-0",
  cellAction: "text-right",
  cellId: "font-mono text-sm",
  cellStatus: "flex items-center gap-2",
  pagination: "flex items-center justify-end space-x-2 py-4",
  empty: "text-center py-10 text-muted-foreground",
  loadingOverlay: "absolute inset-0 flex items-center justify-center bg-background/80",
  interactiveRow: "cursor-pointer hover:bg-muted/50",
  idCell: "font-mono text-xs",
  primaryCell: "font-medium",
};

// Стили для форм
export const formStyles = {
  grid: "grid gap-4",
  gridCols2: "grid grid-cols-1 md:grid-cols-2 gap-4",
  gridCols3: "grid grid-cols-1 md:grid-cols-3 gap-4",
  formItem: "space-y-2",
  formLabel: "text-sm text-muted-foreground block mb-1",
  formDescription: "text-sm text-muted-foreground",
  formMessage: "text-sm font-medium text-destructive",
  formControl: "flex flex-col space-y-1",
  formSection: "space-y-4 border rounded-md p-4",
  formSectionTitle: "text-lg font-medium",
  formActions: "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-4",
  value: "font-medium",
  formDivider: "my-6 border-t",
  formHint: "text-xs text-muted-foreground mt-1",
  formError: "text-sm text-destructive mt-1",
  label: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
  input: "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
};

// Стили для секций
export const sectionStyles = {
  container: "space-y-4",
  header: "flex items-center justify-between",
  title: "text-lg font-medium",
  divider: "my-4 border-t border-border",
};

// Стили для диалогов
export const dialogStyles = {
  content: "sm:max-w-md",
  contentMedium: "sm:max-w-lg",
  contentLarge: "sm:max-w-xl",
  contentXLarge: "sm:max-w-2xl",
  contentStandard: "sm:max-w-3xl",
  standard: "w-[95vw] max-w-[95vw] md:max-w-[66.666vw] max-h-[95vh] overflow-y-auto",
  footer: "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 p-4 sm:p-6 pt-2",
  header: "flex flex-col space-y-1.5 text-center sm:text-left",
  title: "text-lg font-semibold leading-none tracking-tight flex items-center",
  body: "flex flex-col gap-4 p-4 sm:p-6",
  closeButton: "absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
};

// Стили для списков
export const listStyles = {
  container: "space-y-2",
  item: "flex items-center justify-between p-2 rounded-md hover:bg-muted/50",
  itemSelected: "flex items-center justify-between p-2 rounded-md bg-muted/30 hover:bg-muted/50",
};

// Стили для бейджей
export const badgeStyles = {
  status: (color: string) => {
    return `bg-${color}-500 hover:bg-${color}-600 text-white`;
  },
  success: "bg-green-500 hover:bg-green-600 text-white",
  info: "bg-blue-500 hover:bg-blue-600 text-white",
  warning: "bg-yellow-500 hover:bg-yellow-600 text-white",
  destructive: "bg-red-500 hover:bg-red-600 text-white",
  default: "bg-gray-500 hover:bg-gray-600 text-white",
  outlineInfo: "border-blue-200 bg-blue-50 text-blue-700",
  outlineWarning: "border-orange-200 bg-orange-50 text-orange-700",
  outlineDefault: "text-muted-foreground",
};

// Стили для иконок
export const iconStyles = {
  small: "h-4 w-4",
  medium: "h-5 w-5",
  large: "h-6 w-6",
  xlarge: "h-8 w-8",
  animated: "transition-transform duration-200",
  rotate90: "rotate-90",
  rotate180: "rotate-180",
  rotate270: "rotate-270",
};

// Стили для текста
export const textStyles = {
  title: "text-2xl font-bold",
  subtitle: "text-lg text-muted-foreground",
  heading: "text-xl font-semibold",
  heading1: "text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-8 text-slate-600",
  heading2: "text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-slate-600",
  heading3: "text-4xl font-bold mb-4 text-slate-600",
  heading4: "text-2xl font-bold mb-2 text-slate-800",
  body: "text-xl text-slate-500",
  bodySmall: "text-base text-slate-600",
  muted: "text-sm text-muted-foreground",
  label: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
  centered: "text-center",
};

// Стили для отступов и размеров
export const spacingStyles = {
  section: "py-12 md:py-16 lg:py-20",
  subsection: "py-8 md:py-10 lg:py-12",
  group: "py-6 md:py-8",
  padding: "p-6",
  paddingCompact: "p-4",
  paddingY: "py-6",
  paddingX: "px-6",
  paddingYCompact: "py-4",
  paddingXCompact: "px-4",
  marginY: "my-6",
  marginYCompact: "my-4",
  gap: "gap-4",
  gapSmall: "gap-2",
  gapMedium: "gap-6",
  gapLarge: "gap-8",
  sectionHeader: "mb-8 md:mb-12",
};

// Стили для макета страницы
export const layoutStyles = {
  page: "flex flex-col min-h-screen",
  pageHeader: "sticky top-0 z-40 w-full border-b bg-background",
  pageTitle: "text-2xl font-bold tracking-tight",
  pageDescription: "text-muted-foreground",
  pageContent: "flex-1 space-y-4 p-8 pt-6",
  pageFooter: "border-t py-6",
  sidebar: "hidden lg:block border-r h-screen",
  sidebarHeader: "h-14 flex items-center px-4 border-b",
  sidebarContent: "space-y-1 py-2",
  sidebarFooter: "mt-auto p-4 border-t",
  main: "flex-1",
  mainHeader: "flex items-center h-14 gap-4 border-b px-4 lg:px-6",
  mainContent: "flex-1 space-y-4 p-4 md:p-8",
  container: "w-full max-w-4xl mx-auto",
};

// Стили для статусов
export const statusStyles = {
  badge: {
    pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300",
    active: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    completed: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300",
    cancelled: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300",
    draft: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300",
  },
  dot: {
    pending: "bg-yellow-500",
    active: "bg-green-500",
    completed: "bg-blue-500",
    cancelled: "bg-red-500",
    draft: "bg-gray-500",
  },
  text: {
    pending: "text-yellow-600 dark:text-yellow-400",
    active: "text-green-600 dark:text-green-400",
    completed: "text-blue-600 dark:text-blue-400",
    cancelled: "text-red-600 dark:text-red-400",
    draft: "text-gray-600 dark:text-gray-400",
  },
};

// Стили для анимаций
export const animationStyles = {
  fadeIn: "animate-in fade-in duration-300",
  slideIn: "animate-in slide-in-from-bottom-5 duration-300",
  scaleIn: "animate-in zoom-in-95 duration-300",
  spin: "animate-spin",
  pulse: "animate-pulse",
  bounce: "animate-bounce",
};

// Стили для скелетонов загрузки
export const skeletonStyles = {
  base: "animate-pulse bg-muted rounded",
  text: "h-4 w-[250px] animate-pulse bg-muted rounded",
  textShort: "h-4 w-[100px] animate-pulse bg-muted rounded",
  circle: "h-12 w-12 rounded-full animate-pulse bg-muted",
  rectangle: "h-24 w-full animate-pulse bg-muted rounded",
  card: "h-[300px] w-full animate-pulse bg-muted rounded-lg",
  avatar: "h-10 w-10 rounded-full animate-pulse bg-muted",
};

// Стили для дропдаунов и меню
export const menuStyles = {
  content: "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
  item: "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
  separator: "-mx-1 my-1 h-px bg-muted",
  shortcut: "ml-auto text-xs tracking-widest text-muted-foreground",
  label: "px-2 py-1.5 text-sm font-semibold",
};

// Стили для вкладок
export const tabStyles = {
  container: "w-full",
  list: "w-full justify-start h-auto p-1",
  trigger: "whitespace-nowrap",
  content: "mt-0",
  panel: "p-4",
};

// Стили для заголовков
export const headingStyles = {
  h1: "scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl",
  h2: "scroll-m-20 text-2xl font-bold tracking-tight",
  h3: "scroll-m-20 text-xl font-semibold tracking-tight",
  h4: "scroll-m-20 text-lg font-semibold tracking-tight",
  h5: "scroll-m-20 text-base font-semibold tracking-tight",
  h6: "scroll-m-20 text-sm font-semibold tracking-tight",
  muted: "text-sm text-muted-foreground",
  sectionHeader: "text-lg font-medium mb-2",
  feature: "text-xl font-semibold",
  bodySmall: "text-sm",
};

// Стили для тултипов
export const tooltipStyles = {
  content: "z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
};

// Стили для алертов
export const alertStyles = {
  base: "relative w-full rounded-lg border p-4",
  success: "bg-green-50 text-green-900 border-green-200 dark:bg-green-950 dark:text-green-100 dark:border-green-800",
  warning: "bg-yellow-50 text-yellow-900 border-yellow-200 dark:bg-yellow-950 dark:text-yellow-100 dark:border-yellow-800",
  error: "bg-red-50 text-red-900 border-red-200 dark:bg-red-950 dark:text-red-100 dark:border-red-800",
  info: "bg-blue-50 text-blue-900 border-blue-200 dark:bg-blue-950 dark:text-blue-100 dark:border-blue-800",
  title: "mb-1 font-medium leading-none tracking-tight",
  description: "text-sm opacity-90",
};
